import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { useUnsavedChangesWarning } from '@brands/hooks/useUnsavedChangesWarning';
import { useAppDispatch, useAppSelector } from '@brands/hooks/useReduxHook';
import { selectAuth } from '@brands/store/selectors/auth';
import { setSelectedPatient } from '@brands/store/slices/patientFormSlice';
import { setSelectedPatientByAdmin } from '@brands/store/slices/selectedPatientSlice';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { E164Number } from 'libphonenumber-js';
import { isEqual } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IoAdd, IoChevronBack, IoChevronDown, IoEllipsisVertical } from 'react-icons/io5';
import HttpsOutlinedIcon from '@mui/icons-material/HttpsOutlined';
import { toast } from 'react-toastify';
import { useParams, useSearchParams } from 'react-router-dom';

import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import MultiSelectInputMUI from '@brands/Components/Inputs/MuiMultiSelectInput/MultiSelectInputMUI';
import PhoneInput from '@brands/Components/Inputs/MuiTelInput/MuiTelInput';
import { getStaffUserById } from '../../../../services/identity/getStaffUserById';
import { listAllClientTags } from '../../../../services/identity/listAllClientTags';
import { listAllLanguages } from '../../../../services/identity/listAllLanguages';
import { ClientTag, Language, UserProfile } from '../../../../services/identity/types/UserProfile';
import { updateStaffUser } from '../../../../services/identity/updateStaffUser';
import { getClientDivisionOptions } from '../../../../services/identity/clientDivisionOptions';
import { scopeDivisionOptionsToActor } from '../../../../services/identity/clientDivisionActorScope';
import { scopeClientTagOptionsToActor } from '../../../../services/identity/clientTagActorScope';
import { getUserAvatar } from '../../../../services/identity/getUserAvatar';
import { displayErrorDetails } from '../../../../Utils/displayError';
import { genderIdentity, genderOptions, pronounsOptions } from '../../../../Utils/selectButtonOptions';
import { stateOptions } from '../../../../Utils/selectOptions';
import { dateAutoSlashes } from '../../../../Utils/checkAndSetDate';
import { reinviteUserById } from '@brands/services/identity/reinviteUserById';
import { deleteUserById } from '@brands/services/identity/deleteUserById';
import { activateUserById } from '@brands/services/identity/activateUserById';
import { deleteDependent } from '@brands/services/identity/deleteDependent';
import {
  isCollectorDependentActive,
  isCollectorDependentDeleted,
} from '../../../../services/identity/collectorDependents';
import CollectorAddDependentModal from '../../Collector/EditUser/CollectorAddDependentModal';
import ResetPasswordModal from '@brands/Dashboard/MyProfile/Modal/ResetPasswordModal';
import ChangeUserStatusModal from '@brands/Dashboard/MyProfile/Modal/ChangeUserStatusModal';
import UnsavedChangesModal from '@brands/Dashboard/Dashboard/Components/UnsavedChangesModal/UnsavedChangesModal';
import { getStaffReviewProfileBasePath } from '@brands/Utils/reviewProfileBasePath';
import { Loading } from '../../../../Components/LoadingSpinner/Loading';
import AccountSettingsTab from '../../Collector/EditUser/CollectorAccountSettingsTab';
import InsuranceTab from '../../Collector/EditUser/CollectorInsuranceTab';
import MedicalHistoryTab from '../../Collector/EditUser/CollectorMedicalHistoryTab';
import PharmacyTab from '../../Collector/EditUser/CollectorPharmacyTab';
import ProfileCasesSection from '@brands/Dashboard/MyProfile/Components/ProfileCasesSection';
import { config } from '@brands/config/config';

import collectorStyles from '../../Collector/EditUser/collectorEditUser.module.scss';
import addNewUserStyles from '../../Collector/AddNewUser/addNewUser.module.scss';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import { updateVerifiedEmail } from '@brands/services/identity/updateUserEmail';
import { isValidEmail, isValidPhoneNumber } from '../../../../Utils/validation';
import { resolveGuardianDisplayName, resolveProfileDisplayName } from '../../../../Utils/guardianDisplay';

// ─── Constants ───────────────────────────────────────────────────────────────

const allProfileTabs = ['Profile', 'Medical history', 'Insurance', 'Pharmacy', 'Cases', 'Account settings'] as const;
type ProfileTab = (typeof allProfileTabs)[number];
const dependentProfileTabs: ProfileTab[] = ['Profile', 'Medical history', 'Insurance', 'Pharmacy', 'Cases'];

const TAB_QUERY_KEY = 'tab';

// The "User IDs" section (Internal/External ID) is only ever meant for
// staff reviewing a patient's profile -- never for a patient's own My
// Profile. This page is only routed to for these roles today (Patient
// and Collector go elsewhere), but the route itself isn't role-gated, so
// this check is the actual enforcement of "not visible to patients".
const STAFF_ROLES_FOR_USER_IDS: ReadonlySet<string> = new Set([
  UserRoleName.SuperAdmin,
  UserRoleName.Admin,
  UserRoleName.OrganizationAdmin,
  UserRoleName.CareAdvisor,
  UserRoleName.Collector,
]);

const tabToSlug: Record<ProfileTab, string> = {
  Profile: 'profile',
  'Medical history': 'medical-history',
  Insurance: 'insurance',
  Pharmacy: 'pharmacy',
  Cases: 'cases',
  'Account settings': 'account-settings',
};

const slugToTab = Object.fromEntries(allProfileTabs.map((tab) => [tabToSlug[tab], tab])) as Record<string, ProfileTab>;

const relationshipOptions = [
  { value: 'Parent', label: 'Parent' },
  { value: 'Stepparent', label: 'Stepparent' },
  { value: 'Sibling', label: 'Sibling' },
  { value: 'Aunt/Uncle', label: 'Aunt/Uncle' },
  { value: 'Grandparent', label: 'Grandparent' },
  { value: 'Appointed Guardian', label: 'Appointed Guardian' },
  { value: 'Other', label: 'Other' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function profileHasUploadedPhoto(profile: Pick<UserProfile, 'picture_media_id'> | null | undefined): boolean {
  if (!profile?.picture_media_id) return false;
  const id = String(profile.picture_media_id).trim().toLowerCase();
  return id !== '' && id !== '0' && id !== 'null' && id !== 'undefined';
}

/** API returns YYYY-MM-DD; form displays MM/DD/YYYY. */
function dobFromApi(iso: string): string {
  if (!iso) return '';
  const m = moment(iso, ['YYYY-MM-DD', moment.ISO_8601], true);
  return m.isValid() ? m.format('MM/DD/YYYY') : iso;
}

function dobToApi(display: string): string {
  if (!display.trim()) return '';
  const m = moment(display.trim(), 'MM/DD/YYYY', true);
  if (m.isValid()) return m.format('YYYY-MM-DD');
  const m2 = moment(display.trim(), 'YYYY-MM-DD', true);
  return m2.isValid() ? display.trim() : display.trim();
}

// ─── Component ───────────────────────────────────────────────────────────────

const UserProfilePage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const dispatch = useAppDispatch();
  const navigate = useRoleNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userInfo } = useAppSelector(selectAuth);
  const loggedInUserRole = userInfo?.role?.name ?? '';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isInvitedUser, setIsInvitedUser] = useState(false);
  const [resetPasswordConfirmation, setResetPasswordConfirmation] = useState<{
    isOpen: boolean;
    userState: string;
  }>({ isOpen: false, userState: '' });
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [userHeaderMenuAnchor, setUserHeaderMenuAnchor] = useState<null | HTMLElement>(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [deactivateUserModal, setDeactivateUserModal] = useState('');
  const [activateUserModal, setActivateUserModal] = useState('');
  const [deleteDependentModal, setDeleteDependentModal] = useState('');
  const [headerAvatarLoadFailed, setHeaderAvatarLoadFailed] = useState(false);
  const [dependentModalOpen, setDependentModalOpen] = useState(false);
  const [dependentModalTargetId, setDependentModalTargetId] = useState(0);
  const [deactivatedSectionOpen, setDeactivatedSectionOpen] = useState(false);

  // Form state
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [preferredName, setPreferredName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [externalId, setExternalId] = useState('');
  const [gender, setGender] = useState('');
  const [genderIdentityValue, setGenderIdentityValue] = useState('');
  const [pronouns, setPronouns] = useState('');
  const [selectedLanguages, setSelectedLanguages] = useState<Language[]>([]);
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [selectedClientTagId, setSelectedClientTagId] = useState<number | null>(null);
  const [selectedClientDivisionId, setSelectedClientDivisionId] = useState<number | null>(null);
  const [guardianClientTagId, setGuardianClientTagId] = useState<number | null>(null);
  // Tracks whether `guardianClientTagId` reflects a real fetch: it starts
  // `null` the same as "guardian has no tag", so `handleSave` has to tell
  // "not loaded yet" apart from "loaded and empty" to avoid sending a
  // dependent's tag write it never actually looked at.
  const [guardianClientTagLoaded, setGuardianClientTagLoaded] = useState(false);
  const [guardianRelationship, setGuardianRelationship] = useState('');
  const [guardianAddress, setGuardianAddress] = useState<UserProfile['address'] | null>(null);
  const [fetchedGuardianName, setFetchedGuardianName] = useState<string | null>(null);

  // Reference data
  const [allLanguages, setAllLanguages] = useState<Language[]>([]);
  const [allClientTags, setAllClientTags] = useState<ClientTag[]>([]);
  const clientTagOptions = useMemo(
    () =>
      scopeClientTagOptionsToActor(
        allClientTags.map((tag) => ({ id: tag.id, value: String(tag.id), label: tag.name })),
        userInfo,
        selectedClientTagId != null ? [selectedClientTagId] : [],
      ),
    [allClientTags, userInfo, selectedClientTagId],
  );
  const divisionOptions = useMemo(
    () =>
      scopeDivisionOptionsToActor(
        getClientDivisionOptions(allClientTags, selectedClientTagId != null ? [selectedClientTagId] : []).map(
          (option) => ({ id: option.id, value: String(option.value), label: option.label }),
        ),
        userInfo,
        selectedClientDivisionId != null ? [selectedClientDivisionId] : [],
      ),
    [allClientTags, selectedClientTagId, userInfo, selectedClientDivisionId],
  );
  // Division is only required when the actor is themselves confined to a
  // division (not merely whole-tag) -- an unconfined CA/Collector can leave
  // a patient's division unset. A confined actor's own division(s) are the
  // only ones `divisionOptions` ever contains (see `scopeDivisionOptionsToActor`).
  // Also requires the field to actually be rendered (`divisionOptions.length
  // > 0`): a confined actor can still land on a tag with no divisions of its
  // own, or clear their client tag entirely, and without this the
  // requirement would survive with no field left to satisfy it, permanently
  // blocking save.
  //
  // This page is the fallthrough for every non-Collector role (Admin,
  // SuperAdmin, OrganizationAdmin, CareAdvisor), not just CareAdvisor --
  // `scopeDivisionOptionsToActor` only restricts CareAdvisor/Collector, so
  // the required gate has to match that role check or an Admin/SuperAdmin/
  // OrgAdmin whose own profile happens to carry a `divisions` array would
  // get blocked on save while still seeing the full, unconfined list.
  const isConfinedDivisionActor =
    userInfo?.role?.name === UserRoleName.CareAdvisor || userInfo?.role?.name === UserRoleName.Collector;
  const divisionRequired =
    isConfinedDivisionActor &&
    Array.isArray(userInfo?.divisions) &&
    userInfo.divisions.length > 0 &&
    divisionOptions.length > 0;

  // A confined actor with exactly one division has no real choice to make --
  // pre-check it so they don't have to click the only option, while leaving
  // it interactive: they can still clear it, which blocks save (required)
  // until it's re-selected. Never overrides a division the patient already has.
  const autoSelectedDivisionRef = useRef<number | null>(null);
  useEffect(() => {
    if (divisionRequired && divisionOptions.length === 1) {
      const onlyValue = Number(divisionOptions[0].value);
      if (autoSelectedDivisionRef.current !== onlyValue && selectedClientDivisionId == null) {
        autoSelectedDivisionRef.current = onlyValue;
        setSelectedClientDivisionId(onlyValue);
      }
    } else {
      autoSelectedDivisionRef.current = null;
    }
  }, [divisionOptions, selectedClientDivisionId, divisionRequired]);

  /** Snapshot of saved payload for dirty detection. */
  const [savedPayload, setSavedPayload] = useState<object | null>(null);

  /** Shared with whichever tab is mounted so an unsaved-changes save action can submit its form. */
  const formRef = useRef<HTMLFormElement>(null);
  const [medicalHistoryDirty, setMedicalHistoryDirty] = useState(false);
  const [insuranceDirty, setInsuranceDirty] = useState(false);

  const handleCreateNewCase = useCallback(() => {
    if (!user) return;

    const profileRole = user.role?.name ?? UserRoleName.Patient;
    const profileId = user.id;

    dispatch(
      setSelectedPatient({
        value: profileId.toString(),
        label: user.full_name,
      }),
    );
    dispatch(setSelectedPatientByAdmin({ selectedPatient: user }));

    navigate(
      `${getStaffReviewProfileBasePath(userInfo.role.name)}/${profileRole}/${profileId}/create-consultation-for`,
    );
  }, [dispatch, navigate, user, userInfo.role.name]);

  const allDependents = useMemo(() => (user?.dependents ?? []) as UserProfile[], [user?.dependents]);

  const activeDependents = useMemo(
    () => allDependents.filter((dep) => isCollectorDependentActive(dep)),
    [allDependents],
  );

  const deletedDependents = useMemo(
    () => allDependents.filter((dep) => isCollectorDependentDeleted(dep)),
    [allDependents],
  );

  const openAddDependentModal = useCallback(() => {
    setDependentModalTargetId(0);
    setDependentModalOpen(true);
  }, []);

  const closeDependentModal = useCallback(() => {
    setDependentModalOpen(false);
    setDependentModalTargetId(0);
  }, []);

  const handleDependentModalSave = useCallback(
    async (savedDependentId: number, isNew: boolean) => {
      if (!userId) return;
      if (isNew) {
        navigate(`/users/${savedDependentId}`);
      } else {
        try {
          const u = await getStaffUserById(Number(userId), loggedInUserRole);
          setUser(u);
        } catch (e) {
          displayErrorDetails(e);
        }
        closeDependentModal();
      }
    },
    [userId, navigate, closeDependentModal, loggedInUserRole],
  );

  const toggleDeactivatedDependents = useCallback(() => {
    setDeactivatedSectionOpen((open) => !open);
  }, []);

  const buildPayload = useCallback(
    () => ({
      basic_info: {
        first_name: firstName,
        middle_name: middleName,
        last_name: lastName,
        preferred_name: preferredName,
        date_of_birth: dobToApi(dateOfBirth),
        gender,
        gender_identity: genderIdentityValue,
        pronouns,
        languages: selectedLanguages.map((l) => Number(l.id)),
      },
      address: { address1, address2, city, state, zip_code: zipCode },
      phone_number: phoneNumber,
      client_ids: selectedClientTagId ? [selectedClientTagId] : [],
      client_division_ids: selectedClientDivisionId ? [selectedClientDivisionId] : [],
      guardian_relationship: guardianRelationship,
      email: email,
      external_id: externalId,
    }),
    [
      firstName,
      middleName,
      lastName,
      preferredName,
      email,
      dateOfBirth,
      gender,
      genderIdentityValue,
      pronouns,
      selectedLanguages,
      address1,
      address2,
      city,
      state,
      zipCode,
      phoneNumber,
      selectedClientTagId,
      selectedClientDivisionId,
      guardianRelationship,
      externalId,
    ],
  );

  const isProfileDirty = useMemo(() => {
    if (!savedPayload) return false;
    return !isEqual(buildPayload(), savedPayload);
  }, [buildPayload, savedPayload]);

  useEffect(() => {
    setHeaderAvatarLoadFailed(false);
    setDeactivatedSectionOpen(false);
  }, [userId]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const [userData, languages, clientTags] = await Promise.all([
          getStaffUserById(Number(userId), loggedInUserRole),
          listAllLanguages(),
          listAllClientTags(loggedInUserRole),
        ]);

        setUser(userData);
        setFirstName(userData.basic_info?.first_name || '');
        setMiddleName(userData.basic_info?.middle_name || '');
        setLastName(userData.basic_info?.last_name || '');
        setPreferredName(userData.basic_info?.preferred_name || '');
        setDateOfBirth(dobFromApi(userData.basic_info?.date_of_birth || ''));
        setEmail(userData.email || '');
        setPhoneNumber(userData.phone_number || '');
        setGender(userData.basic_info?.gender || '');
        setGenderIdentityValue(userData.basic_info?.gender_identity || '');
        setPronouns(userData.basic_info?.pronouns || '');
        const langs = userData.basic_info?.languages || userData.languages || [];
        const resolvedLangs = langs
          .map((l: Language | number) => (typeof l === 'number' ? languages.find((lang) => lang.id === l) : l))
          .filter(Boolean) as Language[];
        setSelectedLanguages(resolvedLangs);
        setAddress1(userData.address?.address1 || '');
        setAddress2(userData.address?.address2 || '');
        setCity(userData.address?.city || '');
        setState(userData.address?.state || '');
        setZipCode(userData.address?.zip_code || '');
        const selectedClientTagIdForLoad = userData.clients?.[0]?.id ?? null;
        setSelectedClientTagId(selectedClientTagIdForLoad);

        // Fold the single-division auto-select into the initial load itself,
        // computed straight off this fetch's own data rather than the
        // `divisionOptions` memo below (its state isn't set yet at this
        // point). Doing it here -- before the dirty snapshot is taken --
        // keeps a freshly-opened patient with no division from reading as
        // dirty purely from loading the page; the effect further down only
        // needs to handle it changing afterward (e.g. the actor switching
        // client tags).
        let initialDivisionId = userData.divisions?.[0]?.id ?? null;
        if (
          initialDivisionId == null &&
          (userInfo?.role?.name === UserRoleName.CareAdvisor || userInfo?.role?.name === UserRoleName.Collector) &&
          Array.isArray(userInfo?.divisions) &&
          userInfo.divisions.length > 0
        ) {
          const divisionOptionsForLoad = scopeDivisionOptionsToActor(
            getClientDivisionOptions(
              clientTags.nodes,
              selectedClientTagIdForLoad != null ? [selectedClientTagIdForLoad] : [],
            ).map((option) => ({ id: option.id, value: String(option.value), label: option.label })),
            userInfo,
          );
          if (divisionOptionsForLoad.length === 1) {
            initialDivisionId = Number(divisionOptionsForLoad[0].value);
            autoSelectedDivisionRef.current = initialDivisionId;
          }
        }
        setSelectedClientDivisionId(initialDivisionId);
        setGuardianRelationship(userData.guardian_relationship || '');
        setExternalId(userData.external_id || '');

        setAllLanguages(languages);
        setAllClientTags(clientTags.nodes);

        setSavedPayload({
          basic_info: {
            first_name: userData.basic_info?.first_name || '',
            middle_name: userData.basic_info?.middle_name || '',
            last_name: userData.basic_info?.last_name || '',
            preferred_name: userData.basic_info?.preferred_name || '',
            date_of_birth: userData.basic_info?.date_of_birth || '',
            gender: userData.basic_info?.gender || '',
            gender_identity: userData.basic_info?.gender_identity || '',
            pronouns: userData.basic_info?.pronouns || '',
            languages: resolvedLangs.map((l) => Number(l.id)),
          },
          address: {
            address1: userData.address?.address1 || '',
            address2: userData.address?.address2 || '',
            city: userData.address?.city || '',
            state: userData.address?.state || '',
            zip_code: userData.address?.zip_code || '',
          },
          phone_number: userData.phone_number || '',
          client_ids: userData.clients?.[0]?.id ? [userData.clients[0].id] : [],
          client_division_ids: initialDivisionId ? [initialDivisionId] : [],
          guardian_relationship: userData.guardian_relationship || '',
          email: userData.email || '',
          external_id: userData.external_id || '',
        });
      } catch (error) {
        displayErrorDetails(error);
      } finally {
        setLoading(false);
      }
    };
    if (userId) fetchData();
  }, [userId, loggedInUserRole]);

  useEffect(() => {
    let cancelled = false;
    const fetchGuardianAddress = async (): Promise<void> => {
      if (user?.role?.name !== UserRoleName.Dependent || !user?.guardian_id) {
        if (!cancelled) setGuardianAddress(null);
        if (!cancelled) setFetchedGuardianName(null);
        if (!cancelled) setGuardianClientTagLoaded(true);
        return;
      }
      const nestedGuardianName = resolveGuardianDisplayName(user);
      if (!cancelled) setFetchedGuardianName(nestedGuardianName);
      try {
        const guardian = await getStaffUserById(Number(user.guardian_id), loggedInUserRole);
        if (!cancelled) {
          setGuardianAddress(guardian.address ?? null);
          setGuardianClientTagId(guardian.clients?.[0]?.id ?? null);
          setFetchedGuardianName(resolveProfileDisplayName(guardian) || nestedGuardianName);
          setGuardianClientTagLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setGuardianAddress(null);
          setFetchedGuardianName(nestedGuardianName);
          // Left `false` on purpose: an error here means the guardian's
          // tag is genuinely unknown, not empty, and `handleSave` treats
          // "not loaded" as "leave the dependent's tag alone" -- flipping
          // this to `true` would make a failed fetch as destructive as
          // the race it's meant to prevent.
        }
      }
    };
    setGuardianClientTagLoaded(false);
    void fetchGuardianAddress();
    return () => {
      cancelled = true;
    };
  }, [user?.role?.name, user?.guardian_id, loggedInUserRole]);

  const activeTab = useMemo((): ProfileTab => {
    const slug = searchParams.get(TAB_QUERY_KEY)?.toLowerCase() ?? '';
    return slugToTab[slug] ?? 'Profile';
  }, [searchParams]);

  const setActiveTab = useCallback(
    (tab: ProfileTab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(TAB_QUERY_KEY, tabToSlug[tab]);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const isDirty =
    activeTab === 'Profile'
      ? isProfileDirty
      : activeTab === 'Medical history'
      ? medicalHistoryDirty
      : activeTab === 'Insurance'
      ? insuranceDirty
      : false;

  const { showModal, confirmNavigation, saveChanges, hideModal } = useUnsavedChangesWarning(isDirty, formRef);

  const isDependentUser = user?.role?.name === UserRoleName.Dependent;
  const visibleProfileTabs = useMemo(() => {
    const base = isDependentUser ? dependentProfileTabs : allProfileTabs;
    return config.features.multiStage ? base : base.filter((tab) => tab !== 'Cases');
  }, [isDependentUser]);

  useEffect(() => {
    if (isDependentUser && activeTab === 'Account settings') {
      setActiveTab('Profile');
    } else if (activeTab === 'Cases' && !config.features.multiStage) {
      setActiveTab('Profile');
    }
  }, [isDependentUser, activeTab, setActiveTab]);

  const isSameAddressAsGuardian =
    address1 === (guardianAddress?.address1 ?? '') &&
    address2 === (guardianAddress?.address2 ?? '') &&
    city === (guardianAddress?.city ?? '') &&
    state === (guardianAddress?.state ?? '') &&
    zipCode === (guardianAddress?.zip_code ?? '');

  const selectSameAsGuardian = useCallback(
    (isSelect = true): void => {
      if (isSelect) {
        setAddress1(guardianAddress?.address1 ?? '');
        setAddress2(guardianAddress?.address2 ?? '');
        setCity(guardianAddress?.city ?? '');
        setState(guardianAddress?.state ?? '');
        setZipCode(guardianAddress?.zip_code ?? '');
      } else {
        setAddress1('');
        setAddress2('');
        setCity('');
        setState('');
        setZipCode('');
      }
    },
    [guardianAddress],
  );

  const headerDisplayName = useMemo(() => {
    const fromParts = [firstName, lastName].filter(Boolean).join(' ').trim();
    return fromParts || user?.full_name?.trim() || '—';
  }, [firstName, lastName, user?.full_name]);

  const guardianDisplayName = useMemo(
    () => resolveGuardianDisplayName(user) || fetchedGuardianName,
    [fetchedGuardianName, user?.guardian_full_name, user?.guardian],
  );

  const displayStatus = useMemo(() => {
    if (!user) return 'Active';
    if ((user as UserProfile & { deleted_at?: string }).deleted_at) return 'Deactivated';
    return user.status?.trim() || 'Active';
  }, [user]);

  const statusPillClass =
    displayStatus === 'Active'
      ? collectorStyles.statusPillReadOnlyActive
      : displayStatus === 'Deactivated'
      ? collectorStyles.statusPillReadOnlyDeactivated
      : collectorStyles.statusPillReadOnlyOther;

  const headerShowsAvatarImage = useMemo(() => profileHasUploadedPhoto(user ?? undefined), [user]);

  const openUserHeaderMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setUserHeaderMenuAnchor(event.currentTarget);
  }, []);

  const closeUserHeaderMenu = useCallback(() => {
    setUserHeaderMenuAnchor(null);
  }, []);

  const openStatusMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setStatusMenuAnchor(event.currentTarget);
  }, []);

  const closeStatusMenu = useCallback(() => {
    setStatusMenuAnchor(null);
  }, []);

  const handleConfirmDeactivate = useCallback(
    async (targetUserId: number): Promise<void> => {
      try {
        await deleteUserById(targetUserId, loggedInUserRole);
        toast.success('User has been deactivated.', { className: 'fs-unmask' });
        const refreshed = await getStaffUserById(targetUserId, loggedInUserRole);
        setUser(refreshed);
      } catch (error) {
        displayErrorDetails(error);
      }
    },
    [loggedInUserRole],
  );

  const handleConfirmReactivate = useCallback(
    async (targetUserId: number): Promise<void> => {
      try {
        await activateUserById(targetUserId, loggedInUserRole);
        toast.success('User has been activated.', { className: 'fs-unmask' });
        const refreshed = await getStaffUserById(targetUserId, loggedInUserRole);
        setUser(refreshed);
      } catch (error) {
        displayErrorDetails(error);
      }
    },
    [loggedInUserRole],
  );

  const handleConfirmDeleteDependent = useCallback(
    async (_targetUserId: number): Promise<void> => {
      if (!user || !isDependentUser) return;

      if (!user.guardian_id) {
        toast.error('Unable to delete dependent because guardian information is missing.', { className: 'fs-unmask' });
        return;
      }

      try {
        await deleteDependent(Number(user.guardian_id), user.id, loggedInUserRole);
        toast.success('Dependent has been deleted.', { className: 'fs-unmask' });
        navigate('/users');
      } catch (error) {
        displayErrorDetails(error);
      }
    },
    [user, isDependentUser, loggedInUserRole, navigate],
  );

  const getInvitationDomain = (): string => {
    const org =
      user?.organizations.find((o) => o.id !== 'dentistry-one') ??
      user?.organizations.find((o) => o.id === 'dentistry-one');
    if (!org) return '';
    const divisionDomain = org.divisions?.[0]?.domain;
    const practiceDomain = org.practices?.[0]?.domain;
    const currentDomain = window.location.hostname;
    const fallback =
      currentDomain === 'localhost' || currentDomain.startsWith('localhost') ? 'develop.dentistry.one' : currentDomain;
    return divisionDomain || practiceDomain || fallback;
  };

  const inviteUser = async (): Promise<void> => {
    setIsInvitedUser(true);
    try {
      await reinviteUserById(
        userId || '',
        { frontend_website: `https://${getInvitationDomain()}` },
        userInfo.role.name,
      );
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
    setIsInvitedUser(false);
  };

  const validatePayload = (payload: any) => {
    const errors = new Map();
    if (!payload.basic_info.first_name.trim()) {
      errors.set('first_name', 'First name is required');
    }
    if (!payload.basic_info.last_name.trim()) {
      errors.set('last_name', 'Last name is required');
    }
    // The address always goes out with the save, and the API then requires
    // every field but line 2. Checking here gives one message per field, the
    // same as the other profile forms, before the request is sent.
    if (!payload.address.address1?.trim()) {
      errors.set('address1', 'Address is required');
    }
    if (!payload.address.city?.trim()) {
      errors.set('city', 'City is required');
    }
    if (!payload.address.state?.trim()) {
      errors.set('state', 'State is required');
    }
    if (!payload.address.zip_code?.trim()) {
      errors.set('zip_code', 'Zip Code is required');
    }
    if (payload.phone_number && !isValidPhoneNumber(payload.phone_number)) {
      errors.set('phone_number', 'Please enter a valid phone number');
    }
    if (payload.email && !isValidEmail(payload.email)) {
      errors.set('email', 'Please enter a valid email');
    }
    if (payload.external_id && payload.external_id.length > 40) {
      errors.set('external_id', 'External User ID must be 40 characters or fewer.');
    }
    if (!isDependentUser && divisionRequired && !selectedClientDivisionId) {
      errors.set('clientDivision', 'Select a division.');
    }
    if (!isDependentUser && clientTagOptions.length > 0 && !selectedClientTagId) {
      errors.set('clientTag', 'Select a client tag.');
    }
    return errors;
  };

  const handleSave = useCallback(async () => {
    if (!user) return;

    if (isDependentUser && !guardianRelationship.trim()) {
      toast.error('Relationship is required for dependents.', { className: 'fs-unmask' });
      return;
    }

    const payload = buildPayload();
    const errors = validatePayload(payload);
    if (errors.size > 0) {
      errors.forEach((error) => {
        toast.error(`${error}`, { className: 'fs-unmask' });
      });
      return;
    }
    setSaving(true);
    try {
      const updatedUser = await updateStaffUser(
        user.id,
        {
          basic_info: payload.basic_info,
          address: payload.address,
          phone_number: payload.phone_number,
          external_id: payload.external_id,
          // `guardianClientTagId` starts `null`, same as "guardian has no
          // tag" -- so until the separate guardian fetch actually resolves,
          // omit the key entirely rather than send `[]` and wipe a tag
          // that was never looked at. That fetch runs independently of
          // the page's own loading state, so saving right after the page
          // appears ready used to race it.
          client_ids: isDependentUser
            ? guardianClientTagLoaded
              ? guardianClientTagId
                ? [guardianClientTagId]
                : []
              : undefined
            : payload.client_ids,
          client_division_ids: isDependentUser ? [] : selectedClientDivisionId ? [selectedClientDivisionId] : [],
          guardian_relationship: isDependentUser ? payload.guardian_relationship : undefined,
        },
        loggedInUserRole,
      );
      setUser(updatedUser);
      const nextEmail = (payload.email || '').trim();
      if (isDependentUser) {
        if ((user.email || '').trim() !== nextEmail) {
          await updateVerifiedEmail(user.id, { email: nextEmail });
        }
      }
      setDateOfBirth(dobFromApi(updatedUser.basic_info?.date_of_birth || ''));
      setSelectedClientTagId(updatedUser.clients?.[0]?.id ?? null);
      setSelectedClientDivisionId(updatedUser.divisions?.[0]?.id ?? null);
      setSavedPayload(payload);
      toast.success('Your changes have been saved');
    } catch (error) {
      displayErrorDetails(error);
    } finally {
      setSaving(false);
    }
  }, [
    user,
    buildPayload,
    isDependentUser,
    guardianRelationship,
    guardianClientTagId,
    guardianClientTagLoaded,
    loggedInUserRole,
    clientTagOptions,
    selectedClientTagId,
    selectedClientDivisionId,
    divisionRequired,
  ]);

  if (loading) {
    return (
      <div className={collectorStyles.container}>
        <div className={collectorStyles.pageSpinnerWrap}>
          <Loading dotted isHeightAuto />
        </div>
      </div>
    );
  }

  return (
    <div className={collectorStyles.container}>
      <button type="button" className={collectorStyles.backButton} onClick={() => navigate(-1)}>
        <IoChevronBack />
        <span>Back</span>
      </button>

      <div className={collectorStyles.collectorProfileColumn}>
        {user && (
          <>
            <header className={collectorStyles.collectorEditUserHeader}>
              <div className={collectorStyles.collectorEditUserHeaderIdentity}>
                <div className={collectorStyles.collectorEditUserHeaderAvatar} aria-hidden={!headerDisplayName}>
                  {headerShowsAvatarImage && user.id && !headerAvatarLoadFailed ? (
                    <img
                      src={getUserAvatar(user.id)}
                      alt=""
                      className={`fs-exclude ${collectorStyles.collectorEditUserHeaderAvatarImg}`}
                      onError={() => setHeaderAvatarLoadFailed(true)}
                    />
                  ) : (
                    <span className={collectorStyles.collectorEditUserHeaderAvatarInitials}>
                      {headerDisplayName ? getInitials(headerDisplayName) : '?'}
                    </span>
                  )}
                </div>
                <div className={collectorStyles.collectorEditUserHeaderText}>
                  <h1 className={collectorStyles.collectorEditUserHeaderName}>
                    {headerDisplayName}
                    {isDependentUser && <span className={collectorStyles.dependentLabel}> (dependent)</span>}
                  </h1>
                  <div className={collectorStyles.userStatusRow}>
                    <span className={collectorStyles.userStatusLabel}>User status:</span>
                    {isDependentUser ? (
                      <span className={`${collectorStyles.statusPillReadOnly} ${statusPillClass}`}>
                        {displayStatus}
                      </span>
                    ) : (
                      <>
                        <button
                          type="button"
                          className={`${collectorStyles.statusPillReadOnly} ${statusPillClass} ${collectorStyles.statusPillAction}`}
                          onClick={openStatusMenu}
                          aria-label="Change user status"
                          aria-controls={statusMenuAnchor ? 'ca-user-status-menu' : undefined}
                          aria-haspopup="true"
                          aria-expanded={statusMenuAnchor ? 'true' : undefined}
                        >
                          <span>{displayStatus}</span>
                          <IoChevronDown className={collectorStyles.statusPillChevron} aria-hidden />
                        </button>
                        <Menu
                          id="ca-user-status-menu"
                          anchorEl={statusMenuAnchor}
                          open={Boolean(statusMenuAnchor)}
                          onClose={closeStatusMenu}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        >
                          {user.status === 'Deactivated' ? (
                            <MenuItem
                              sx={{ fontFamily: 'Poppins, sans-serif', fontSize: 14 }}
                              onClick={() => {
                                closeStatusMenu();
                                setActivateUserModal('reactivate');
                              }}
                            >
                              Activate
                            </MenuItem>
                          ) : (
                            <MenuItem
                              sx={{ fontFamily: 'Poppins, sans-serif', fontSize: 14 }}
                              onClick={() => {
                                closeStatusMenu();
                                setDeactivateUserModal('deactivated');
                              }}
                            >
                              Deactivate
                            </MenuItem>
                          )}
                        </Menu>
                      </>
                    )}
                  </div>
                  {isDependentUser && guardianDisplayName && user.guardian_id && (
                    <div className={collectorStyles.userStatusRow}>
                      <span className={collectorStyles.userStatusLabel}>Guardian:</span>
                      <button
                        type="button"
                        className={collectorStyles.guardianLink}
                        onClick={() => navigate(`/users/${user.guardian_id}`)}
                      >
                        {guardianDisplayName}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className={collectorStyles.collectorEditUserHeaderActions}>
                <button
                  type="button"
                  className={collectorStyles.collectorEditUserCreateCaseLink}
                  onClick={handleCreateNewCase}
                >
                  <IoAdd aria-hidden />
                  Create new case
                </button>
                {user.role.name !== UserRoleName.Dependent && (
                  <>
                    <IconButton
                      aria-label="More actions"
                      aria-controls={userHeaderMenuAnchor ? 'ca-user-header-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={userHeaderMenuAnchor ? 'true' : undefined}
                      onClick={openUserHeaderMenu}
                    >
                      <IoEllipsisVertical />
                    </IconButton>
                    <Menu
                      id="ca-user-header-menu"
                      anchorEl={userHeaderMenuAnchor}
                      open={Boolean(userHeaderMenuAnchor)}
                      onClose={closeUserHeaderMenu}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      <MenuItem
                        onClick={() => {
                          closeUserHeaderMenu();
                          void inviteUser();
                        }}
                        disabled={['Active', 'Pending', 'Deactivated'].includes(user?.status) || isInvitedUser}
                      >
                        <ListItemIcon>
                          <IoAdd />
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ fontFamily: 'Poppins, sans-serif', fontSize: 14 }}>
                          Invite user
                        </ListItemText>
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          closeUserHeaderMenu();
                          setResetPasswordConfirmation({ isOpen: true, userState: 'Active' });
                        }}
                        disabled={['Deactivated', 'Not Invited', 'Invited'].includes(user?.status) || resetEmailSent}
                      >
                        <ListItemIcon>
                          <HttpsOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ fontFamily: 'Poppins, sans-serif', fontSize: 14 }}>
                          Reset password
                        </ListItemText>
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </div>
            </header>

            <Alert
              severity="info"
              className={addNewUserStyles.patientProfileNotice}
              sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
            >
              You are editing <strong>{headerDisplayName}</strong>&apos;s profile.
            </Alert>
          </>
        )}

        <div className={collectorStyles.tabsCollector} role="tablist" aria-label="Profile sections">
          {visibleProfileTabs.map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={activeTab === tab}
              className={`${collectorStyles.tabCollector} ${
                activeTab === tab ? collectorStyles.tabCollectorActive : ''
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === 'Profile' && (
          <Box className={addNewUserStyles.formRoot}>
            <Box
              component="form"
              ref={formRef}
              onSubmit={(e) => {
                e.preventDefault();
                void handleSave();
              }}
              onKeyDown={(e) => {
                // Only a real `<form>` since the unsaved-changes modal needs to submit it; guard
                // against the native browser behavior of implicitly submitting on Enter in a
                // text field, which didn't exist when this was a plain `<Box>`.
                if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
                  e.preventDefault();
                }
              }}
              className={addNewUserStyles.form}
              style={{ gap: '32px' }}
            >
              {/* Basic information */}
              <Box className={addNewUserStyles.row} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <TextField
                  name="firstName"
                  label="First name"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  wrapperClassName={addNewUserStyles.inputWrap}
                />
                <TextField
                  name="middleName"
                  label="Middle name (optional)"
                  value={middleName}
                  onChange={(e) => setMiddleName(e.target.value)}
                  wrapperClassName={addNewUserStyles.inputWrap}
                />
                <TextField
                  name="lastName"
                  label="Last name"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  wrapperClassName={addNewUserStyles.inputWrap}
                />
              </Box>

              <Box className={addNewUserStyles.row} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <TextField
                  name="preferredName"
                  label="Preferred name (optional)"
                  value={preferredName}
                  onChange={(e) => setPreferredName(e.target.value)}
                  wrapperClassName={addNewUserStyles.inputWrap}
                />
                <TextField
                  name="dateOfBirth"
                  label="Date of birth"
                  value={dateOfBirth}
                  onChange={(e) =>
                    dateAutoSlashes(e, 'dateOfBirth', (key: string, val: string) => {
                      if (key === 'dateOfBirth') setDateOfBirth(val);
                    })
                  }
                  maxLength={10}
                  wrapperClassName={addNewUserStyles.inputWrap}
                />
              </Box>

              <Box className={addNewUserStyles.row} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <TextField
                  name="email"
                  label="Email address"
                  value={email}
                  disabled={!isDependentUser}
                  onChange={(e) => setEmail(e.target.value)}
                  wrapperClassName={addNewUserStyles.inputWrap}
                />
                <Box className={addNewUserStyles.inputWrap}>
                  <PhoneInput
                    value={phoneNumber as E164Number}
                    name="phone_number"
                    onChange={(v) => setPhoneNumber(v ?? '')}
                    label="Phone number"
                    wrapperClassName={addNewUserStyles.inputWrap}
                  />
                </Box>
              </Box>

              <Box className={addNewUserStyles.row} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Box className={addNewUserStyles.inputWrap}>
                  <MUISelectInput
                    name="gender"
                    label="Gender assigned at birth"
                    value={gender}
                    onChange={(v: string) => setGender(String(v))}
                    options={genderOptions}
                  />
                </Box>
                <Box className={addNewUserStyles.inputWrap}>
                  <MUISelectInput
                    name="genderIdentity"
                    label="Gender identity"
                    value={genderIdentityValue}
                    onChange={(v: string) => setGenderIdentityValue(String(v))}
                    options={genderIdentity}
                  />
                </Box>
                <Box className={addNewUserStyles.inputWrap}>
                  <MUISelectInput
                    name="pronouns"
                    label="Preferred pronouns (optional)"
                    value={pronouns}
                    onChange={(v: string) => setPronouns(String(v))}
                    options={pronounsOptions}
                  />
                </Box>
              </Box>

              <Box className={addNewUserStyles.row} sx={{ mb: 2 }}>
                <MultiSelectInputMUI
                  name="languages"
                  label="Languages"
                  required
                  withId
                  styledTag
                  value={selectedLanguages.map((l) => String(l.id))}
                  onChange={(ids: string[]) =>
                    setSelectedLanguages(allLanguages.filter((l) => ids.includes(String(l.id))))
                  }
                  options={allLanguages.map((l) => ({ id: l.id, value: l.id, label: l.name }))}
                  wrapperClassName={addNewUserStyles.inputWrap}
                />
              </Box>

              {/* Dependent-only: relation + same-address */}
              {isDependentUser && (
                <>
                  <Box className={addNewUserStyles.row} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <Box className={addNewUserStyles.inputWrap}>
                      <MUISelectInput
                        name="guardianRelationship"
                        label="Relation"
                        required
                        value={guardianRelationship}
                        onChange={(v: string) => setGuardianRelationship(String(v))}
                        options={relationshipOptions}
                      />
                    </Box>
                  </Box>
                  <Box className={collectorStyles.sameAddressCheckboxRow}>
                    <Checkbox
                      checked={isSameAddressAsGuardian}
                      onChange={(_e, checked) => selectSameAsGuardian(checked)}
                      disabled={!guardianAddress?.address1}
                    />
                    <span className={collectorStyles.sameAddressCheckboxLabel}>Same address as guardian</span>
                  </Box>
                </>
              )}

              {/* Address */}
              <Box className={addNewUserStyles.row} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <TextField
                  name="address1"
                  label="Address"
                  required
                  value={address1}
                  disabled={isSameAddressAsGuardian}
                  onChange={(e) => setAddress1(e.target.value)}
                  wrapperClassName={addNewUserStyles.inputWrap}
                />
                <TextField
                  name="address2"
                  label="Address line 2 (optional)"
                  value={address2}
                  disabled={isSameAddressAsGuardian}
                  onChange={(e) => setAddress2(e.target.value)}
                  wrapperClassName={addNewUserStyles.inputWrap}
                />
              </Box>

              <Box className={addNewUserStyles.row} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <TextField
                  name="city"
                  label="City"
                  required
                  value={city}
                  disabled={isSameAddressAsGuardian}
                  onChange={(e) => setCity(e.target.value)}
                  wrapperClassName={addNewUserStyles.inputWrap}
                />
                <Box className={addNewUserStyles.inputWrap}>
                  <MUISelectInput
                    name="state"
                    label="State"
                    required
                    value={state}
                    disabled={isSameAddressAsGuardian}
                    onChange={(v: string) => setState(String(v))}
                    options={stateOptions}
                  />
                </Box>
                <TextField
                  name="zipCode"
                  label="Zip code"
                  required
                  value={zipCode}
                  disabled={isSameAddressAsGuardian}
                  onChange={(e) => setZipCode(e.target.value)}
                  wrapperClassName={addNewUserStyles.inputWrap}
                />
              </Box>

              {/* Client tags — patients only; dependents inherit from guardian */}
              {!isDependentUser && clientTagOptions.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '20px', mb: 2 }}>
                    Client tags
                  </Typography>
                  <p className={addNewUserStyles.clientTagsDescription}>
                    Select the client tag for this patient&apos;s Dentistry.One services.
                  </p>
                  <Box className={addNewUserStyles.inputWrap}>
                    <MUISelectInput
                      name="clientTag"
                      label="Client tag(s)"
                      required
                      value={selectedClientTagId?.toString() ?? ''}
                      options={clientTagOptions}
                      onChange={(v: string) => {
                        setSelectedClientTagId(v ? Number(v) : null);
                        setSelectedClientDivisionId(null);
                      }}
                    />
                  </Box>
                  {selectedClientTagId != null && divisionOptions.length > 0 && (
                    <Box className={addNewUserStyles.inputWrap}>
                      <MUISelectInput
                        name="clientDivision"
                        label="Division"
                        required={divisionRequired}
                        value={selectedClientDivisionId?.toString() ?? ''}
                        options={divisionOptions}
                        onChange={(v: string) => setSelectedClientDivisionId(v ? Number(v) : null)}
                      />
                    </Box>
                  )}
                </>
              )}

              {/* User IDs: staff-only, never shown on a patient's own My Profile */}
              {STAFF_ROLES_FOR_USER_IDS.has(loggedInUserRole) && (
                <>
                  <h2 className={collectorStyles.sectionTitle}>User IDs</h2>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                    <TextField
                      name="externalUserId"
                      label="External User ID"
                      value={externalId}
                      onChange={(e) => setExternalId(e.target.value)}
                      maxLength={40}
                      wrapperClassName={addNewUserStyles.inputWrap}
                    />
                    <TextField
                      name="internalUserId"
                      label="Internal User ID"
                      value={String(user.id)}
                      disabled
                      wrapperClassName={addNewUserStyles.inputWrap}
                    />
                  </Box>
                </>
              )}

              {/* Dependents (patient only) */}
              {!isDependentUser && (
                <>
                  <div className={collectorStyles.dependentsHeader}>
                    <h2 className={collectorStyles.sectionTitle} style={{ margin: 0 }}>
                      Dependents
                    </h2>
                    <button type="button" className={collectorStyles.addDependentBtn} onClick={openAddDependentModal}>
                      <IoAdd />
                      Add dependent
                    </button>
                  </div>
                  {activeDependents.length > 0 && (
                    <div className={collectorStyles.dependentList}>
                      {activeDependents.map((dep: UserProfile) => (
                        <div key={dep.id} className={collectorStyles.dependentRow}>
                          <div className={collectorStyles.dependentAvatar}>
                            {dep.full_name ? getInitials(dep.full_name) : '?'}
                          </div>
                          <div className={collectorStyles.dependentInfo}>
                            <span className={collectorStyles.dependentName}>{dep.full_name}</span>
                            <button
                              type="button"
                              className={collectorStyles.editProfileLink}
                              onClick={() => navigate(`/users/${dep.id}`)}
                            >
                              Edit profile
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {deletedDependents.length > 0 && (
                    <>
                      <button
                        type="button"
                        className={collectorStyles.viewDeactivatedLink}
                        onClick={toggleDeactivatedDependents}
                      >
                        {deactivatedSectionOpen ? 'Hide deactivated dependents' : 'View deactivated dependents'}
                      </button>
                      {deactivatedSectionOpen && (
                        <div className={collectorStyles.deactivatedDependentsSection}>
                          <div className={collectorStyles.dependentList}>
                            {deletedDependents.map((dep) => (
                              <div
                                key={dep.id}
                                className={`${collectorStyles.dependentRow} ${collectorStyles.dependentRowDeactivated}`}
                              >
                                <div className={collectorStyles.dependentAvatar}>
                                  {dep.full_name ? getInitials(dep.full_name) : '?'}
                                </div>
                                <div className={collectorStyles.dependentInfo}>
                                  <span className={collectorStyles.dependentName}>{dep.full_name || '—'}</span>
                                  <span className={collectorStyles.dependentStatusMuted}>Deleted</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              {/* Actions */}
              <Box className={addNewUserStyles.actions} sx={{ mt: 2 }}>
                <Button
                  type="button"
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || !isProfileDirty}
                  className={addNewUserStyles.saveChangesButton}
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
                {isDependentUser && (
                  <button
                    type="button"
                    className={collectorStyles.dependentDeleteBtn}
                    onClick={() => setDeleteDependentModal('delete')}
                  >
                    Delete dependent
                  </button>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {activeTab === 'Medical history' && (
          <MedicalHistoryTab
            user={user}
            userRole={loggedInUserRole}
            formRef={formRef}
            setIsDirty={setMedicalHistoryDirty}
          />
        )}
        {activeTab === 'Insurance' && (
          <InsuranceTab user={user} userRole={loggedInUserRole} formRef={formRef} setIsDirty={setInsuranceDirty} />
        )}
        {activeTab === 'Pharmacy' && <PharmacyTab user={user} userRole={loggedInUserRole} />}
        {activeTab === 'Cases' && config.features.multiStage && user && <ProfileCasesSection patientId={user.id} />}
        {activeTab === 'Account settings' && !isDependentUser && (
          <AccountSettingsTab user={user} userRole={loggedInUserRole} />
        )}
      </div>

      {resetPasswordConfirmation.isOpen && (
        <ResetPasswordModal
          setOpenModal={setResetPasswordConfirmation}
          disableButton={() => setResetEmailSent(true)}
          userId={Number(userId)}
          userEmail={user?.email || ''}
          userName={user?.full_name || ''}
          isDeactivated={user?.status === 'Deactivated'}
          userState={resetPasswordConfirmation.userState}
        />
      )}

      {deactivateUserModal && user && (
        <ChangeUserStatusModal
          setOpenModal={setDeactivateUserModal}
          disableButton={() => undefined}
          userId={user.id}
          userName={user.full_name || user.email || 'User'}
          status="deactivated"
          onConfirmDeactivate={handleConfirmDeactivate}
        />
      )}

      {activateUserModal && user && (
        <ChangeUserStatusModal
          setOpenModal={setActivateUserModal}
          disableButton={() => undefined}
          userId={user.id}
          userName={user.full_name || user.email || 'User'}
          status="reactivate"
          onConfirmReactivate={handleConfirmReactivate}
        />
      )}

      {deleteDependentModal && user && (
        <ChangeUserStatusModal
          setOpenModal={setDeleteDependentModal}
          disableButton={() => undefined}
          userId={user.id}
          userName={user.full_name || user.email || 'User'}
          status="delete-dependent"
          onConfirmDeactivate={handleConfirmDeleteDependent}
        />
      )}

      {user && !isDependentUser && (
        <CollectorAddDependentModal
          open={dependentModalOpen}
          onClose={closeDependentModal}
          guardianUser={user}
          dependentId={dependentModalTargetId}
          onDependentSaved={handleDependentModalSave}
        />
      )}

      {showModal && (
        <UnsavedChangesModal
          isOpen={showModal}
          onConfirm={confirmNavigation}
          saveChange={saveChanges}
          loading={false}
          hideModal={hideModal}
        />
      )}
    </div>
  );
};

export default UserProfilePage;
